import { useSelectPanel } from '@/storybook/components/FormCollection/components/DropdownList/SelectPanelContext';
import { TREE_ITEM_TOOLTIP_ID } from '@/storybook/components/FormCollection/components/constants';
import styles from './TreeItem.module.css';
import type { TreeItemRootProps } from './TreeItem.types';
import {
  TreeItemContextProvider,
  resolveEffectiveDisabled,
  useTreeItemContext,
  type TreeItemContextValue,
} from './TreeItemContext';
import { TreeItemCaretSlot, TreeItemCheckboxSlot, TreeItemLabelRegion } from './TreeItemSegments';
import { useTreeItemRowState } from './useTreeItemRowState';

export function TreeItemRoot({
  children,
  label,
  labelContent,
  tooltipText,
  forceTooltip = false,
  level = 1,
  variant = 'tree',
  hasChildren = false,
  isExpanded = false,
  onToggleExpand,
  showCheckbox = false,
  checked = false,
  onCheck,
  icon,
  badge,
  endContent,
  size,
  tabIndex,
  isSelected = false,
  'isDisabled': isDisabledProp,
  onClick,
  'data-testid': dataTestId,
  checkboxDataTestId,
  caretDataTestId,
  'className': classNameProp,
}: TreeItemRootProps) {
  const panel = useSelectPanel();
  const isDisabled = resolveEffectiveDisabled(isDisabledProp, panel);

  const rowState = useTreeItemRowState({
    variant,
    hasChildren,
    isExpanded,
    onToggleExpand,
    showCheckbox,
    checked,
    onCheck,
    isSelected,
    isDisabled,
    tabIndex,
    onClick,
    className: classNameProp,
  });

  const {
    labelId,
    checkTruncation,
    handleItemClick,
    handleKeyDown,
    ariaRole,
    ariaChecked,
    rootClassName,
    rowTabIndex,
  } = rowState;

  const contextValue: TreeItemContextValue = {
    ...rowState,
    label,
    labelContent,
    tooltipText,
    forceTooltip,
    icon,
    badge,
    endContent,
    showCheckbox,
    checked,
    onCheck,
    hasChildren,
    isExpanded,
    isDisabled,
    isSelected,
    level,
    caretDataTestId,
    checkboxDataTestId,
  };

  return (
    <TreeItemContextProvider value={contextValue}>
      <div
        className={rootClassName}
        data-level={level}
        data-is-selected={isSelected ? 'true' : 'false'}
        data-size={size}
        data-testid={dataTestId}
        onClick={handleItemClick}
        onKeyDown={handleKeyDown}
        onMouseEnter={checkTruncation}
        tabIndex={rowTabIndex}
        role={ariaRole}
        aria-checked={ariaChecked}
        aria-selected={!showCheckbox ? isSelected : undefined}
        aria-disabled={isDisabled}
        aria-expanded={hasChildren ? isExpanded : undefined}
        aria-labelledby={labelId}
      >
        {children}
      </div>
    </TreeItemContextProvider>
  );
}

TreeItemRoot.displayName = 'TreeItem.Root';

export function TreeItemCaret() {
  const {
    showCaretColumn,
    hasChildren,
    isExpanded,
    isDisabled,
    caretButtonClassName,
    label,
    handleCaretClick,
    caretDataTestId,
  } = useTreeItemContext();

  return (
    <TreeItemCaretSlot
      showCaretColumn={showCaretColumn}
      hasChildren={hasChildren}
      isExpanded={isExpanded}
      isDisabled={isDisabled}
      caretButtonClassName={caretButtonClassName}
      label={label}
      onCaretClick={handleCaretClick}
      caretDataTestId={caretDataTestId}
    />
  );
}

TreeItemCaret.displayName = 'TreeItem.Caret';

export function TreeItemCheckbox() {
  const { showCheckbox, checked, onCheck, isDisabled, checkboxDataTestId } = useTreeItemContext();

  return (
    <TreeItemCheckboxSlot
      showCheckbox={showCheckbox}
      checked={checked}
      onCheck={onCheck}
      isDisabled={isDisabled}
      checkboxDataTestId={checkboxDataTestId}
    />
  );
}

TreeItemCheckbox.displayName = 'TreeItem.Checkbox';

export function TreeItemIcon() {
  const { icon } = useTreeItemContext();
  if (!icon) {
    return null;
  }
  return (
    <span className={styles.iconSlot} aria-hidden="true">
      {icon}
    </span>
  );
}

TreeItemIcon.displayName = 'TreeItem.Icon';

export function TreeItemLabel() {
  const { labelRef, labelId, label, labelContent, tooltipText, forceTooltip, isTruncated } = useTreeItemContext();

  // A forced tooltip carries the cascading "no longer applicable" hint, which
  // anchors to the badge slot instead. The label keeps only its truncation
  // tooltip so the hint does not emerge from the middle of the row.
  const labelTooltipText = forceTooltip ? label : tooltipText;

  return (
    <TreeItemLabelRegion
      labelRef={labelRef}
      labelId={labelId}
      label={label}
      labelContent={labelContent}
      tooltipText={labelTooltipText}
      isTruncated={isTruncated}
    />
  );
}

TreeItemLabel.displayName = 'TreeItem.Label';

export function TreeItemEnd() {
  const { endContent } = useTreeItemContext();
  if (!endContent) {
    return null;
  }
  return <div className={styles.endContent}>{endContent}</div>;
}

TreeItemEnd.displayName = 'TreeItem.End';

export function TreeItemBadge() {
  const { badge, handleBadgeClick, tooltipText, forceTooltip } = useTreeItemContext();
  if (!badge) {
    return null;
  }
  const showBadgeTooltip = Boolean(forceTooltip) && Boolean(tooltipText);
  return (
    <div
      className={styles.badgeSlot}
      onClick={handleBadgeClick}
      data-testid="treeItemBadgeSlot"
      data-tooltip-id={showBadgeTooltip ? TREE_ITEM_TOOLTIP_ID : undefined}
      data-tooltip-content={showBadgeTooltip ? tooltipText : undefined}
      data-tooltip-place={showBadgeTooltip ? 'left' : undefined}
    >
      {badge}
    </div>
  );
}

TreeItemBadge.displayName = 'TreeItem.Badge';
